import { useState, useCallback, useContext } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Lock, Download, Database, Users, FileText, Activity, Loader2, Pencil, Presentation, BarChart3 } from 'lucide-react';
import { DemoModeContext } from '@/App';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FounderAdmin({ open, onClose, onEditEncounter }) {
  const [passcode, setPasscode] = useState('');
  const [authenticated, setAuthenticated] = useState(false);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [savingDemoMode, setSavingDemoMode] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const { demoMode, setDemoMode } = useContext(DemoModeContext);

  const fetchAnalytics = useCallback(async (pc) => {
    const code = pc || passcode;
    if (!code) return;
    setAnalyticsLoading(true);
    try {
      const res = await axios.get(`${API}/admin/demo-analytics`, { params: { passcode: code, days: 30 } });
      setAnalytics(res.data);
    } catch (err) {
      toast.error('Failed to load analytics');
    } finally {
      setAnalyticsLoading(false);
    }
  }, [passcode]);

  const handleToggleDemoMode = useCallback(async (next) => {
    setSavingDemoMode(true);
    try {
      await axios.post(`${API}/admin/settings`, { demoMode: next, passcode });
      setDemoMode(next);
      toast.success(next ? 'YC Demo Mode ON — Agent Chat hidden' : 'YC Demo Mode OFF — Agent Chat restored');
      fetchAnalytics(passcode);
    } catch (err) {
      toast.error('Failed to update demo mode: ' + (err.response?.data?.error || 'unknown error'));
    } finally {
      setSavingDemoMode(false);
    }
  }, [passcode, setDemoMode, fetchAnalytics]);

  const handleLogin = useCallback(async () => {
    setError('');
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/verify`, { passcode });
      if (res.data.success) {
        setAuthenticated(true);
        const statsRes = await axios.get(`${API}/admin/stats`);
        setStats(statsRes.data);
        fetchAnalytics(passcode);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid passcode');
    } finally {
      setLoading(false);
    }
  }, [passcode, fetchAnalytics]);

  const handleClose = useCallback(() => {
    onClose();
    // Don't reset auth state on close (session memory until refresh as per spec)
  }, [onClose]);

  const handleDownloadPDF = useCallback(() => {
    // Full "Founder Report" PDF — every encounter + its SOAP note. Direct
    // navigation is more reliable than the old hidden-iframe trigger.
    window.open(`${API}/admin/pdf`, '_blank', 'noopener,noreferrer');
  }, []);

  const handleDownloadContactsCSV = useCallback(() => {
    const url = `${API}/admin/contacts-csv?passcode=${encodeURIComponent(passcode)}`;
    const a = document.createElement('a');
    a.href = url;
    a.download = '';
    document.body.appendChild(a);
    a.click();
    setTimeout(() => document.body.removeChild(a), 5000);
  }, [passcode]);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[80vh]" data-testid="admin-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lock className="w-5 h-5 text-[#2563EB]" /> Admin / Maintenance
          </DialogTitle>
        </DialogHeader>

        {!authenticated ? (
          <div className="py-6" data-testid="admin-login-form">
            <p className="text-sm text-slate-600 mb-4">Enter the founder passcode to access system diagnostics.</p>
            <div className="flex gap-2">
              <Input
                type="password"
                placeholder="Enter passcode"
                value={passcode}
                onChange={e => { setPasscode(e.target.value); setError(''); }}
                onKeyDown={e => e.key === 'Enter' && handleLogin()}
                className="bg-slate-50 rounded-xl"
                data-testid="admin-passcode-input"
              />
              <Button onClick={handleLogin} disabled={loading} className="bg-[#2563EB] hover:bg-blue-700" data-testid="admin-login-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Access'}
              </Button>
            </div>
            {error && <p className="text-sm text-red-600 mt-2" data-testid="admin-error">{error}</p>}
          </div>
        ) : (
          <ScrollArea className="max-h-[60vh]">
            <div className="space-y-6" data-testid="admin-dashboard">
              {/* YC Demo Mode Toggle */}
              <div className={`rounded-xl p-4 border-2 flex items-center justify-between gap-3 ${demoMode ? 'bg-emerald-50 border-emerald-400' : 'bg-slate-50 border-slate-200'}`} data-testid="demo-mode-toggle-block">
                <div className="flex items-center gap-3 flex-1">
                  <Presentation className={`w-6 h-6 ${demoMode ? 'text-emerald-600' : 'text-slate-400'}`} />
                  <div>
                    <div className="font-semibold text-sm text-slate-900">YC Demo Mode {demoMode && <span className="text-emerald-600 ml-1">· ACTIVE</span>}</div>
                    <p className="text-[11px] text-slate-500 leading-snug">When ON, hides Patient 24/7 Virtual Assistant button. Prevents judges from triggering the 10–15s Claude path. Persists until switched off.</p>
                  </div>
                </div>
                <Switch
                  checked={demoMode}
                  onCheckedChange={handleToggleDemoMode}
                  disabled={savingDemoMode}
                  data-testid="demo-mode-switch"
                />
              </div>

              {/* Two canonical numbers — clearly separated, never conflated (founder spec 2026-07-15) */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="canonical-numbers">
                <div className="rounded-xl p-4 border-2 border-blue-300 bg-blue-50/60" data-testid="real-use-counter-card">
                  <div className="text-[11px] font-semibold text-blue-700 uppercase tracking-wider">Self-Test Encounter Counter · immutable</div>
                  <div className="text-4xl font-bold text-blue-900 mt-1" data-testid="real-use-counter-value">{stats?.realUseCounter ?? stats?.currentCounter ?? 0}</div>
                  <p className="text-[11px] text-blue-700/80 mt-1 leading-snug">Monotonic. +1 only on a committed source=user intake — founder/team synthetic tests · no PHI (MVP build). Never moved by demos, retries or exploration.</p>
                </div>
                <div className="rounded-xl p-4 border-2 border-emerald-300 bg-emerald-50/60" data-testid="total-platform-card">
                  <div className="text-[11px] font-semibold text-emerald-700 uppercase tracking-wider">Total Platform Encounters · disclosure</div>
                  <div className="text-4xl font-bold text-emerald-900 mt-1" data-testid="total-platform-value">{stats?.totalPlatformEncounters ?? stats?.totalEncounters ?? 0}</div>
                  <p className="text-[11px] text-emerald-700/80 mt-1 leading-snug">
                    Self-test {stats?.encountersBreakdown?.selfTest ?? 0} · System-test {stats?.encountersBreakdown?.systemTest ?? 0} · Demo {stats?.encountersBreakdown?.demo ?? 0}. PGH-TEST-* / exploration auto-excluded &amp; auto-deleted.
                  </p>
                </div>
              </div>

              {/* Physicians Actively Validating — registered AND submitted feedback.
                  Distinct from "agreed to validate" (registration only). Founder metric 2026-07-20. */}
              <div className="rounded-xl p-4 border-2 border-teal-300 bg-teal-50/60 flex items-center justify-between gap-3" data-testid="validator-active-card">
                <div>
                  <div className="text-[11px] font-semibold text-teal-700 uppercase tracking-wider">Physicians Actively Validating</div>
                  <p className="text-[11px] text-teal-700/80 mt-1 leading-snug">Registered <em>and</em> submitted structured validation feedback on the platform's deterministic clinical output. Distinct from "agreed to validate" (registration only).</p>
                </div>
                <div className="text-4xl font-bold text-teal-900 shrink-0" data-testid="validator-active-value">{stats?.validatorActive ?? 0}</div>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {[
                  { icon: <Database className="w-5 h-5" />, label: 'System Tests', value: stats?.systemTests ?? 0 },
                  { icon: <Users className="w-5 h-5" />, label: 'Self-Test Users', value: stats?.realUsers ?? 0 },
                  { icon: <Activity className="w-5 h-5" />, label: 'Total Encounters', value: stats?.totalEncounters ?? 0 },
                  { icon: <Users className="w-5 h-5" />, label: 'Registrations', value: stats?.totalRegistrations ?? 0 },
                  { icon: <Activity className="w-5 h-5" />, label: 'Counter', value: stats?.currentCounter ?? 0 },
                  { icon: <FileText className="w-5 h-5" />, label: 'Saved Reports', value: stats?.savedReports ?? 0 },
                ].map((stat, i) => (
                  <div key={i} className="bg-slate-50 rounded-xl p-3 border border-slate-200 text-center" data-testid={`admin-stat-${i}`}>
                    <div className="text-[#2563EB] flex justify-center mb-1">{stat.icon}</div>
                    <div className="text-2xl font-bold text-slate-900">{stat.value}</div>
                    <div className="text-[10px] text-slate-500 uppercase tracking-wider">{stat.label}</div>
                  </div>
                ))}
              </div>

              <div className="text-xs text-slate-500">
                Note: Saved report count is independent from encounter numbering.
              </div>

              {/* Demo Analytics Panel */}
              <div className="rounded-xl border-2 border-indigo-200 bg-indigo-50/40 p-4" data-testid="demo-analytics-panel">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-indigo-600" />
                    <h4 className="font-semibold text-sm text-slate-900">Demo Analytics</h4>
                    <span className="text-[10px] text-slate-500">(for investor traction reporting)</span>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => fetchAnalytics(passcode)}
                    disabled={analyticsLoading}
                    className="h-7 text-xs"
                    data-testid="analytics-refresh-btn"
                  >
                    {analyticsLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Refresh'}
                  </Button>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
                  <div className="bg-white rounded-lg p-2.5 border border-slate-200 text-center" data-testid="analytics-total">
                    <div className="text-xl font-bold text-slate-900">{analytics?.totalEncounters ?? 0}</div>
                    <div className="text-[10px] text-slate-500 uppercase tracking-wider">Total</div>
                  </div>
                  <div className="bg-white rounded-lg p-2.5 border-2 border-emerald-300 text-center" data-testid="analytics-demo">
                    <div className="text-xl font-bold text-emerald-700">{analytics?.demoEncounters ?? 0}</div>
                    <div className="text-[10px] text-emerald-700 uppercase tracking-wider">Demo Mode</div>
                  </div>
                  <div className="bg-white rounded-lg p-2.5 border-2 border-blue-300 text-center" data-testid="analytics-real">
                    <div className="text-xl font-bold text-blue-700">{analytics?.realPatientEncounters ?? 0}</div>
                    <div className="text-[10px] text-blue-700 uppercase tracking-wider">Real Patients</div>
                  </div>
                  <div className="bg-white rounded-lg p-2.5 border border-slate-200 text-center" data-testid="analytics-tests">
                    <div className="text-xl font-bold text-slate-600">{analytics?.systemTestEncounters ?? 0}</div>
                    <div className="text-[10px] text-slate-500 uppercase tracking-wider">System Tests</div>
                  </div>
                </div>

                <div className="text-xs font-semibold text-slate-700 mb-1.5">Last 30 days breakdown</div>
                {analytics?.byDate?.length > 0 ? (
                  <div className="max-h-48 overflow-y-auto rounded-lg border border-slate-200 bg-white">
                    <table className="w-full text-xs" data-testid="analytics-by-date-table">
                      <thead className="bg-slate-100 sticky top-0">
                        <tr>
                          <th className="text-left px-2.5 py-1.5 font-semibold text-slate-700">Date</th>
                          <th className="text-right px-2.5 py-1.5 font-semibold text-slate-700">Total</th>
                          <th className="text-right px-2.5 py-1.5 font-semibold text-emerald-700">Demo</th>
                          <th className="text-right px-2.5 py-1.5 font-semibold text-blue-700">Real</th>
                          <th className="text-right px-2.5 py-1.5 font-semibold text-slate-500">Tests</th>
                        </tr>
                      </thead>
                      <tbody>
                        {analytics.byDate.map((row, i) => (
                          <tr key={row.date} className={i % 2 ? 'bg-slate-50' : ''} data-testid={`analytics-row-${row.date}`}>
                            <td className="px-2.5 py-1.5 font-mono">{row.date}</td>
                            <td className="px-2.5 py-1.5 text-right font-semibold">{row.total}</td>
                            <td className="px-2.5 py-1.5 text-right text-emerald-700 font-semibold">{row.demo}</td>
                            <td className="px-2.5 py-1.5 text-right text-blue-700 font-semibold">{row.real}</td>
                            <td className="px-2.5 py-1.5 text-right text-slate-500">{row.tests}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-xs text-slate-500 italic">No encounters yet.</p>
                )}

                <div className="text-[10px] text-slate-500 mt-2 leading-snug">
                  Methodology: Each encounter is tagged with the YC Demo Mode state at submission time. "Real Patients" = non-system-test encounters captured while Demo Mode was OFF.
                </div>
              </div>

              {/* Recent Encounters */}
              <div>
                <h4 className="font-semibold text-sm text-slate-900 mb-2">Recent Encounters</h4>
                {stats?.recentEncounters?.length > 0 ? (
                  <div className="space-y-1.5">
                    {stats.recentEncounters.map((enc, i) => (
                      <div key={i} className="flex items-center justify-between p-2.5 rounded-lg bg-slate-50 border border-slate-100 text-xs gap-2">
                        <div className="flex-1 min-w-0">
                          <span className="font-semibold text-slate-800">{enc.encounterId}</span>
                          <span className="text-slate-500 ml-2">{enc.visitDate?.slice(0, 19)}</span>
                          <div className="text-slate-600 truncate">{enc.chiefComplaint}</div>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => window.open(`${API}/encounter/${enc.encounterId}/soap-pdf`, '_blank', 'noopener,noreferrer')}
                            className="h-7 px-2 text-xs border-blue-300 text-blue-700 hover:bg-blue-50"
                            data-testid={`admin-pdf-${enc.encounterId}`}
                          >
                            <Download className="w-3 h-3 mr-1" /> PDF
                          </Button>
                          {onEditEncounter && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => { onEditEncounter(enc.encounterId); onClose(); }}
                              className="h-7 px-2 text-xs border-amber-300 text-amber-700 hover:bg-amber-50"
                              data-testid={`admin-edit-${enc.encounterId}`}
                            >
                              <Pencil className="w-3 h-3 mr-1" /> Edit
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-slate-500">No encounters recorded yet.</p>
                )}
              </div>

              {/* Download full-report PDF (all encounters + SOAP notes) */}
              <Button onClick={handleDownloadPDF} variant="outline" className="w-full rounded-xl" data-testid="admin-download-pdf">
                <Download className="w-4 h-4 mr-2" /> Download All Encounters + SOAP (PDF)
              </Button>

              {/* Download Contacts CSV (lead export — NOT the SOAP PDF) */}
              <Button onClick={handleDownloadContactsCSV} className="w-full rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="admin-download-contacts-csv">
                <Download className="w-4 h-4 mr-2" /> Download Contacts CSV (Physician / Visitor Leads)
              </Button>
            </div>
          </ScrollArea>
        )}
      </DialogContent>
    </Dialog>
  );
}
